import { useMutation, useQuery, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import {
	commitTicketImport,
	deleteTicketImport,
	getEventSuggestions,
	getTicketImport,
	listTicketImports,
	previewTicketImport,
	updateTicketImportMapping,
	uploadTicketImport,
} from './services';
import {
	CommitResponse,
	EventSuggestionsResponse,
	PreviewResponse,
	TicketImport,
	UpdateMappingPayload,
	UploadResponse,
} from './types';

export const useUploadTicketImport = (
	options?: UseMutationOptions<UploadResponse, unknown, { file: File; sourcePlatform?: string }>
) => useMutation(({ file, sourcePlatform }) => uploadTicketImport(file, sourcePlatform), options);

export const useTicketImport = (id: number | null, options?: UseQueryOptions<TicketImport>) =>
	useQuery<TicketImport>(['ticket-import', id], () => getTicketImport(id as number), {
		enabled: id !== null,
		...options,
	});

export const useTicketImportsList = (
	params?: { status?: string; limit?: number },
	options?: UseQueryOptions<TicketImport[]>
) =>
	useQuery<TicketImport[]>(['ticket-imports', params?.status, params?.limit], () => listTicketImports(params), options);

export const useUpdateMapping = (
	id: number,
	options?: UseMutationOptions<TicketImport, unknown, UpdateMappingPayload>
) => useMutation((payload) => updateTicketImportMapping(id, payload), options);

export const useEventSuggestions = (id: number | null, options?: UseQueryOptions<EventSuggestionsResponse>) =>
	useQuery<EventSuggestionsResponse>(
		['ticket-importer-event-suggestions', id],
		() => getEventSuggestions(id as number),
		{ enabled: id !== null, staleTime: 30_000, ...options }
	);

export const usePreviewTicketImport = (id: number, options?: UseMutationOptions<PreviewResponse, unknown, void>) =>
	useMutation(() => previewTicketImport(id), options);

export const useCommitTicketImport = (id: number, options?: UseMutationOptions<CommitResponse, unknown, void>) =>
	useMutation(() => commitTicketImport(id), options);

export const useDeleteTicketImport = (options?: UseMutationOptions<void, unknown, number>) =>
	useMutation((id) => deleteTicketImport(id), options);
